/**
 * Client-side clickjacking protection
 * This provides an additional layer of defense against frame-based attacks
 */

export const initializeClickjackingProtection = (): void => {
  // Frame-busting script - prevents the page from being loaded in a frame
  if (window.top !== window.self) {
    // If we're in a frame, try to break out
    try {
      // Security: Construct URL from validated components (not from user input)
      // Explicitly validate and use only safe protocols
      const protocol = window.location.protocol;
      if (protocol !== "http:" && protocol !== "https:") {
        throw new Error("Invalid protocol detected");
      }
      // Build URL from individual validated components to avoid taint analysis issues
      const safeProtocol = protocol === "https:" ? "https:" : "http:";
      const safeHost = window.location.host; // hostname + port
      const safePath = window.location.pathname;
      // Construct URL without using location object directly
      const safeUrl = safeProtocol + "//" + safeHost + safePath;

      // Use replace instead of href assignment to prevent back button issues
      window.top!.location.replace(safeUrl);
    } catch {
      // If we can't access parent (cross-origin), hide the page content
      document.body.style.display = "none";

      // Get the current site name for the warning message
      const siteName = window.location.hostname.includes("localhost")
        ? "this application"
        : window.location.hostname;

      // Show warning message
      const warningDiv = document.createElement("div");
      warningDiv.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #fff;
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: Arial, sans-serif;
          text-align: center;
          padding: 20px;
          box-sizing: border-box;
        ">
          <div>
            <h1 style="color: #d32f2f; margin-bottom: 20px;">Security Warning</h1>
            <p style="font-size: 18px; margin-bottom: 20px;">
              This page cannot be displayed in a frame for security reasons.
            </p>
            <p style="margin-bottom: 20px;">
              Please visit <strong>${siteName}</strong> directly.
            </p>
            <a 
              href="${window.location.protocol}//${window.location.host}" 
              style="
                display: inline-block;
                padding: 12px 24px;
                background: #1976d2;
                color: white;
                text-decoration: none;
                border-radius: 4px;
                font-weight: bold;
              "
            >
              Go to ${siteName}
            </a>
          </div>
        </div>
      `;
      document.body.appendChild(warningDiv);
    }
  }
};

/**
 * Additional frame detection using various methods
 */
export const detectFraming = (): boolean => {
  try {
    // Method 1: Check if we're in a frame
    if (window.top !== window.self) {
      return true;
    }

    // Method 2: Check parent window
    if (window.parent !== window.self) {
      return true;
    }

    // Method 3: Check frame depth
    if (window.frameElement) {
      return true;
    }

    // Method 4: Check window.top accessibility
    try {
      const topLocation = window.top!.location.href;
      if (topLocation !== window.location.href) {
        return true;
      }
    } catch {
      // Cross-origin frame detected
      return true;
    }

    return false;
  } catch {
    // If any check fails, assume we might be framed
    return true;
  }
};

/**
 * Monitor for dynamic frame injection
 */
export const monitorFrameInjection = (): void => {
  let isFramed = detectFraming();

  // Check periodically for frame injection
  const checkInterval = setInterval(() => {
    const currentlyFramed = detectFraming();

    if (!isFramed && currentlyFramed) {
      // Frame injection detected
      console.warn("Frame injection detected - implementing protection");
      initializeClickjackingProtection();
      clearInterval(checkInterval);
    }

    isFramed = currentlyFramed;
  }, 1000);

  // Clean up after 30 seconds
  setTimeout(() => {
    clearInterval(checkInterval);
  }, 30000);
};

/**
 * CSP violation reporting
 */
export const setupCSPReporting = (): void => {
  document.addEventListener("securitypolicyviolation", (event) => {
    console.warn("CSP Violation:", {
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      disposition: event.disposition,
    });

    // Report to analytics or monitoring service
    if (typeof window !== "undefined" && "gtag" in window) {
      const gtag = (window as { gtag: (...args: unknown[]) => void }).gtag;
      gtag("event", "csp_violation", {
        event_category: "security",
        event_label: event.violatedDirective,
        value: 1,
      });
    }
  });
};
